/**
 * Экран «Графики смен» менеджера (manager design v3 §8.1).
 *
 * Сетка сотрудник × дни недели с цветными ячейками смен (день=charcoal,
 * ночь=slate-blue, активная/идёт=зелёная рамка, пропущена=peach). Навигация
 * по неделям ← →, фильтр по отделу, легенда. «+ Смена» — быстрое добавление
 * смены (на основе шаблона: префилл времени/отдела). Мобайл 375 — вертикальный
 * список смен дня (карточки по дизайну).
 *
 * Бэк: internal/scheduling (GET schedules?department=, GET schedules/{id}/shifts,
 * POST /v1/scheduling/shifts) + шаблоны смен (/v1/scheduling/shift-templates).
 */
import { useMemo, useState } from 'react';
import { Card, Skeleton } from '@hsc/ui';
import type { ModuleProps } from '../../routes/Workspace.js';
import {
  addDays,
  useScheduleGrid,
  weekStart,
  type GridShift,
} from './useScheduleGrid.js';
import { AddShiftForm } from './AddShiftForm.js';

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const;

const SHIFT_STATUS_LABELS: Readonly<Record<string, string>> = {
  scheduled: 'Запланирована',
  confirmed: 'Подтверждена',
  in_progress: 'Идёт',
  completed: 'Завершена',
  missed: 'Пропущена',
  swapped: 'Передана',
  cancelled: 'Отменена',
};

function fmtDay(d: Date): string {
  return `${WEEKDAYS[(d.getDay() + 6) % 7]} ${d.getDate()}`;
}

function fmtWeekRange(from: Date): string {
  const to = addDays(from, 6);
  const m = (x: Date): string =>
    x.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
  return `${m(from)} — ${m(to)}`;
}

function hhmm(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

/** Цвет ячейки по статусу/времени смены: ночная смена — slate-blue. */
function shiftTone(gs: GridShift): { code: string; cls: string } {
  const { shift } = gs;
  if (shift.status === 'missed' || shift.status === 'cancelled') {
    return { code: '!', cls: 'sched-cell--conflict' };
  }
  const startHour = new Date(shift.scheduled_start).getHours();
  // Ночная смена начинается с 19:00 и позже (или до 06:00).
  const isNight = startHour >= 19 || startHour < 6;
  return { code: isNight ? 'N' : 'D', cls: isNight ? 'sched-cell--night' : 'sched-cell--day' };
}

export function SchedulesPage({ canManage = false }: ModuleProps): JSX.Element {
  const [weekFrom, setWeekFrom] = useState<Date>(() => weekStart(new Date()));
  const [deptFilter, setDeptFilter] = useState<string | undefined>(undefined);
  const [adding, setAdding] = useState(false);

  const grid = useScheduleGrid(weekFrom, deptFilter);
  const { days, staff, byStaff, departments, totalShifts, isLoading, isError } = grid;

  const prevWeek = (): void => setWeekFrom((w) => addDays(w, -7));
  const nextWeek = (): void => setWeekFrom((w) => addDays(w, 7));

  const todayIdx = useMemo(() => {
    const now = new Date();
    const t0 = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return days.findIndex((d) => d.getTime() === t0);
  }, [days]);

  // «+ Смена» — ОТДЕЛЬНЫЙ экран (форма вместо сетки), не расширение сверху
  // (feedback_every_form_is_a_separate_screen). Возврат — «Назад» снизу формы.
  if (canManage && adding) {
    return (
      <section data-testid="schedules-page">
        <div className="ta-topbar">
          <h1 className="hsc-page__title">Новая смена</h1>
        </div>
        <AddShiftForm onClose={() => setAdding(false)} departmentId={deptFilter} />
      </section>
    );
  }

  return (
    <section data-testid="schedules-page">
      <div className="sched-head">
        <div>
          <h1 className="hsc-page__title">Графики смен</h1>
          <p className="ta-muted" data-testid="schedules-summary">
            Неделя {fmtWeekRange(weekFrom)} · смен: {totalShifts}
          </p>
        </div>
        <div className="sched-toolbar">
          <button
            type="button"
            className="ta-back"
            onClick={prevWeek}
            data-testid="week-prev"
            aria-label="Предыдущая неделя"
          >
            ← Неделя
          </button>
          <button
            type="button"
            className="ta-back"
            onClick={nextWeek}
            data-testid="week-next"
            aria-label="Следующая неделя"
          >
            Неделя →
          </button>
          {canManage && (
            <button
              type="button"
              className="fleet-tab is-active sched-add-btn"
              onClick={() => setAdding(true)}
              data-testid="shift-add-toggle"
            >
              + Смена
            </button>
          )}
        </div>
      </div>

      <div className="fleet-floors" data-testid="sched-dept-filter">
        <button
          type="button"
          className={`fleet-chip${deptFilter === undefined ? ' is-active' : ''}`}
          onClick={() => setDeptFilter(undefined)}
          data-testid="sched-dept-all"
        >
          Все отделы
        </button>
        {departments.map((d) => (
          <button
            key={d.id}
            type="button"
            className={`fleet-chip${deptFilter === d.id ? ' is-active' : ''}`}
            onClick={() => setDeptFilter(d.id)}
            data-testid={`sched-dept-${d.id}`}
          >
            {d.name}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div data-testid="schedules-loading">
          <Skeleton height={220} radius={16} />
        </div>
      ) : isError ? (
        <Card>
          <p role="alert" data-testid="schedules-error">
            Не удалось загрузить расписание. Попробуйте обновить страницу.
          </p>
        </Card>
      ) : staff.length === 0 ? (
        <Card>
          <p data-testid="schedules-empty">
            Сотрудники ещё не заведены — добавьте структуру отделов и персонал в разделе
            «Персонал», затем создайте смены.
          </p>
        </Card>
      ) : (
        <>
          {/* Десктоп/планшет — сетка сотрудник × дни. */}
          <div
            className="sched-grid"
            data-testid="sched-grid"
            role="table"
            aria-label="Сетка смен по сотрудникам и дням"
          >
            <div className="sched-cell sched-cell--head sched-cell--name">Сотрудник</div>
            {days.map((d, i) => (
              <div
                key={d.toISOString()}
                className={`sched-cell sched-cell--head${i === todayIdx ? ' sched-cell--today' : ''}`}
              >
                {fmtDay(d)}
              </div>
            ))}

            {staff.map((s) => {
              const shifts = byStaff.get(s.id) ?? [];
              const byDay = new Map<number, GridShift>();
              for (const gs of shifts) byDay.set(gs.dayIndex, gs);
              return (
                <div key={s.id} className="sched-row" data-testid={`sched-row-${s.id}`}>
                  <div className="sched-cell sched-cell--name" title={s.label}>
                    {s.label}
                  </div>
                  {days.map((_d, i) => {
                    const gs = byDay.get(i);
                    if (!gs) {
                      return <div key={i} className="sched-cell sched-cell--off" />;
                    }
                    const tone = shiftTone(gs);
                    return (
                      <div
                        key={i}
                        className={`sched-cell ${tone.cls}`}
                        data-testid={`sched-shift-${gs.shift.id}`}
                        title={`${hhmm(gs.shift.scheduled_start)}–${hhmm(gs.shift.scheduled_end)} · ${
                          SHIFT_STATUS_LABELS[gs.shift.status] ?? gs.shift.status
                        }`}
                      >
                        {tone.code}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          <div className="sched-legend" data-testid="sched-legend">
            <span>
              <i className="sched-swatch sched-swatch--day" /> день (D)
            </span>
            <span>
              <i className="sched-swatch sched-swatch--night" /> ночь (N)
            </span>
            <span>
              <i className="sched-swatch sched-swatch--conflict" /> пропуск (!)
            </span>
          </div>

          {/* Мобайл — вертикальный список смен (по дизайну: карточка на смену). */}
          <div className="sched-mobile" data-testid="sched-mobile">
            {days.map((d, i) => {
              const dayShifts = staff
                .flatMap((s) =>
                  (byStaff.get(s.id) ?? [])
                    .filter((gs) => gs.dayIndex === i)
                    .map((gs) => ({ staff: s, gs })),
                );
              if (dayShifts.length === 0) return null;
              return (
                <div key={d.toISOString()} className="sched-mobile__day">
                  <div className="sched-mobile__date">{fmtDay(d)}</div>
                  {dayShifts.map(({ staff: s, gs }) => {
                    const tone = shiftTone(gs);
                    return (
                      <div
                        key={gs.shift.id}
                        className={`sched-mobile__card ${tone.cls}`}
                        data-testid={`sched-mcard-${gs.shift.id}`}
                      >
                        <strong>{s.label}</strong>
                        <span className="sched-mobile__time">
                          {tone.code} {hhmm(gs.shift.scheduled_start)}–
                          {hhmm(gs.shift.scheduled_end)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}
